/* VoiceSearchFab — floating mic button that opens the global voice
 * navigation overlay. Hidden on Kitchen View, when the admin kill switch
 * is off, or when the user hasn't opted in via Profile.
 *
 * The overlay is a bottom sheet (mobile-first) that shows:
 *   • Live transcript of what the mic is hearing
 *   • "Last action" chip once a command resolves
 *   • Disambiguation list when multiple matches hit
 *   • Inline help sheet listing every supported phrase
 */
import { Link } from "react-router-dom";
import { Microphone, MicrophoneSlash, Waveform, Question, Gear, ArrowRight } from "@phosphor-icons/react";
import { useVoiceSearch } from "@/contexts/VoiceSearchContext";
import useFocusTrap from "@/hooks/useFocusTrap";

export default function VoiceSearchFab() {
  const vs = useVoiceSearch();
  const { adminAllowed, prefsEnabled, onKitchenRoute, overlayMode,
          transcript, candidates, candidatePage, showMoreCandidates, showPrevCandidates, lastAction, micBlocked, recentChips,
          wakeArmed, micPaused,
          openOverlay, closeOverlay, pickCandidate } = vs;

  const trapRef = useFocusTrap(overlayMode !== "idle", closeOverlay);

  // Hard gates — never render if admin disabled, user opted out, or we're
  // inside Kitchen View (which owns the mic for cook commands).
  if (!adminAllowed || !prefsEnabled || onKitchenRoute) return null;

  const open = overlayMode !== "idle";
  const fabTitle = open
    ? "Voice overlay open — speak now"
    : wakeArmed
      ? "Listening for \"Hey Recipe\" — tap to speak"
      : micPaused
        ? "Mic paused — tap to resume listening"
        : "Tap to speak";

  return (
    <>
      {/* Passive-listen pulse ring — shows only when wake word is armed
          but overlay is still closed. Pointer-events-none so it never
          swallows the FAB click. */}
      {!open && wakeArmed && (
        <>
          <span
            data-testid="voice-wake-pulse"
            aria-hidden="true"
            className="fixed bottom-6 right-6 z-[54] w-14 h-14 rounded-full bg-emerald-400/60 animate-ping pointer-events-none"
          />
          <span
            data-testid="voice-wake-dot"
            aria-hidden="true"
            className="fixed bottom-[72px] right-[72px] z-[56] w-3 h-3 rounded-full bg-emerald-400 ring-2 ring-white pointer-events-none"
          />
        </>
      )}

      {/* Floating mic button */}
      <div className="group fixed bottom-6 right-6 z-[55]">
        <button
          type="button"
          onClick={openOverlay}
          aria-label={fabTitle}
          aria-describedby={micPaused ? "voice-paused-tip" : undefined}
          data-testid="voice-search-fab"
          data-paused={micPaused ? "true" : "false"}
          className={`relative w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-transform hover:scale-105 focus-visible:ring-4 focus-visible:ring-rf-amber/60 ${open ? "bg-rf-ink text-rf-amber" : wakeArmed ? "bg-rf-orange text-white ring-2 ring-emerald-400" : micPaused ? "bg-rf-orange text-white ring-2 ring-red-500/70" : "bg-rf-orange text-white"}`}
        >
          {open
            ? <Waveform size={24} weight="duotone" aria-hidden="true" />
            : micPaused
              ? <MicrophoneSlash size={24} weight="duotone" aria-hidden="true" />
              : <Microphone size={24} weight="duotone" aria-hidden="true" />}
          <span className="sr-only">Voice search</span>
          {!open && micPaused && (
            <span
              data-testid="voice-paused-dot"
              aria-hidden="true"
              className="absolute -top-0.5 -left-0.5 w-3.5 h-3.5 rounded-full bg-red-600 ring-2 ring-white"
            />
          )}
        </button>
        {!open && micPaused && (
          <span
            id="voice-paused-tip"
            role="tooltip"
            data-testid="voice-paused-tooltip"
            className="pointer-events-none absolute bottom-full right-0 mb-3 whitespace-nowrap rounded-lg bg-rf-ink px-3 py-2 text-xs font-medium text-white shadow-lg opacity-0 translate-y-1 transition-[opacity,transform] duration-150 group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:opacity-100 group-focus-within:translate-y-0"
          >
            <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-2 align-middle" aria-hidden="true" />
            Mic paused — tap to resume listening
          </span>
        )}
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[56] bg-rf-ink/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
          onClick={closeOverlay}
          data-testid="voice-search-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Voice search overlay"
        >
          <div
            ref={trapRef}
            className="w-full max-w-md bg-rf-cream rounded-3xl shadow-2xl p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={closeOverlay}
              aria-label="Close voice overlay"
              data-testid="voice-search-close"
              className="absolute top-3 right-3 w-9 h-9 rounded-full bg-rf-cream-alt hover:bg-rf-amber font-bold text-lg flex items-center justify-center"
            >✕</button>

            {/* HELP MODE */}
            {overlayMode === "help" ? (
              <VoiceHelpContent />
            ) : (
              <>
                <p className="overline text-rf-orange mb-1">Voice search</p>
                <h2 className="font-display text-2xl font-black text-rf-ink mb-4 leading-tight">
                  {overlayMode === "resolving" ? "Looking that up…" :
                   overlayMode === "confirming" ? "Which one did you mean?" :
                   "I'm listening"}
                </h2>

                {micBlocked && (
                  <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-800">
                    Microphone access was blocked. Click the lock icon in your browser bar and allow mic access.
                  </div>
                )}

                {/* Live transcript */}
                <div className="min-h-[60px] px-4 py-3 rounded-xl bg-white border border-rf-ink/10 mb-4">
                  {recentChips && recentChips.length > 0 && (
                    <div
                      className="flex flex-wrap gap-1.5 mb-2"
                      data-testid="voice-recent-chips"
                      aria-live="polite"
                    >
                      {recentChips.map((c) => (
                        <span
                          key={c.id}
                          data-testid={`voice-chip-${c.tone}`}
                          className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full border ${
                            c.tone === "warn"
                              ? "bg-amber-50 border-amber-300 text-amber-900"
                              : c.tone === "info"
                                ? "bg-sky-50 border-sky-300 text-sky-900"
                                : "bg-emerald-50 border-emerald-300 text-emerald-900"
                          }`}
                        >
                          {c.tone === "ok" ? "✓ " : c.tone === "warn" ? "? " : "• "}
                          {c.label}
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center min-h-[36px]">
                    {transcript ? (
                      <p className="text-rf-ink font-medium">&ldquo;{transcript}&rdquo;</p>
                    ) : (
                      <p className="text-rf-ink-soft italic text-sm">
                        {overlayMode === "resolving" ? "Searching your library…" : "Try: “my cookbooks”, “cook paella”, “scan a recipe”"}
                      </p>
                    )}
                  </div>
                </div>

                {/* Disambiguation list */}
                {overlayMode === "confirming" && candidates.length > 0 && (
                  <ul className="space-y-2 mb-4" data-testid="voice-search-candidates">
                    {candidates.map((c, i) => (
                      <li key={`${c.kind}-${c.path}-${i}`}>
                        <button
                          type="button"
                          onClick={() => pickCandidate(i)}
                          data-testid={`voice-search-candidate-${i}`}
                          className="w-full text-left p-3 rounded-xl bg-white hover:bg-rf-amber/20 border border-rf-ink/10 flex items-center gap-3"
                        >
                          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-rf-orange/90 text-white font-bold text-sm flex items-center justify-center">{i + 1}</span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-bold text-rf-ink truncate">{c.label}</span>
                            <span className="block text-xs text-rf-ink-soft">
                              {c.subtitle || `${c.kind} · ${c.owned ? "yours" : "from directory"}`}
                              {c.action !== "open" ? ` · will ${c.action}` : ""}
                            </span>
                          </span>
                          <ArrowRight size={14} weight="bold" className="text-rf-ink-soft" aria-hidden="true" />
                        </button>
                      </li>
                    ))}
                    {candidatePage.total > candidates.length && (
                      <li className="flex items-center justify-between gap-2 pt-1" data-testid="voice-search-candidates-pager">
                        <button
                          type="button" onClick={showPrevCandidates} disabled={candidatePage.start === 0}
                          data-testid="voice-search-candidates-prev"
                          className="text-xs font-bold text-rf-ink-soft hover:text-rf-ink disabled:opacity-30"
                        >&larr; Back</button>
                        <span className="text-xs text-rf-ink-soft" data-testid="voice-search-candidates-range">
                          {candidatePage.start + 1}&ndash;{candidatePage.start + candidates.length} of {candidatePage.total}
                        </span>
                        <button
                          type="button" onClick={showMoreCandidates}
                          disabled={candidatePage.start + candidates.length >= candidatePage.total}
                          data-testid="voice-search-candidates-more"
                          className="text-xs font-bold text-rf-orange hover:underline disabled:opacity-30"
                        >More &rarr;</button>
                      </li>
                    )}
                    <li className="text-xs text-rf-ink-soft text-center pt-1">
                      Say &ldquo;number one&rdquo;, &ldquo;second&rdquo;
                      {candidatePage.start + candidates.length < candidatePage.total ? <>, &ldquo;more&rdquo; for the next set</> : null}
                      {candidatePage.start > 0 ? <>, &ldquo;back&rdquo;</> : null}
                      {" "}or &ldquo;cancel&rdquo;
                    </li>
                  </ul>
                )}

                {lastAction && overlayMode !== "confirming" && (
                  <p className="text-xs text-rf-ink-soft italic mt-2">{lastAction}</p>
                )}

                <div className="flex items-center justify-between gap-2 mt-4">
                  <button
                    type="button"
                    onClick={() => vs.dispatchCommand("help")}
                    className="chip !bg-rf-cream-alt text-xs inline-flex items-center gap-1"
                    data-testid="voice-search-help-trigger"
                  >
                    <Question size={12} weight="bold" aria-hidden="true" />
                    What can I say?
                  </button>
                  <Link to="/profile" className="chip !bg-rf-cream-alt text-xs inline-flex items-center gap-1" onClick={closeOverlay}>
                    <Gear size={12} weight="bold" aria-hidden="true" />
                    Voice settings
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function VoiceHelpContent() {
  return (
    <>
      <p className="overline text-rf-orange mb-1">Voice search · phrase guide</p>
      <h2 className="font-display text-2xl font-black text-rf-ink mb-4 leading-tight">What can I say?</h2>
      <div className="space-y-4 text-sm text-rf-ink max-h-[55vh] overflow-y-auto pr-1">
        <div>
          <p className="font-bold text-rf-ink mb-1">Wake up anywhere</p>
          <p className="text-rf-ink-soft text-xs leading-relaxed">If &ldquo;Always listen for the wake word&rdquo; is on, say any of these: <b>&ldquo;Hey Recipe&rdquo;</b>, <b>&ldquo;Hey Recipe Filer&rdquo;</b> or <b>&ldquo;Hey Filer&rdquo;</b>.</p>
        </div>
        <div>
          <p className="font-bold text-rf-ink mb-1">Jump to a page</p>
          <ul className="list-disc list-inside text-rf-ink-soft text-xs space-y-1">
            <li>&ldquo;home&rdquo; / &ldquo;take me home&rdquo;</li>
            <li>&ldquo;dashboard&rdquo; / &ldquo;my dashboard&rdquo;</li>
            <li>&ldquo;my cookbooks&rdquo;, &ldquo;my recipes&rdquo;</li>
            <li>&ldquo;scan a recipe&rdquo; / &ldquo;add recipe&rdquo;</li>
            <li>&ldquo;shopping list&rdquo;, &ldquo;meal planner&rdquo;</li>
            <li>&ldquo;kitchen help&rdquo; / &ldquo;glossary&rdquo;</li>
            <li>&ldquo;what is arrowroot?&rdquo;, &ldquo;what does fold mean?&rdquo; (Kitchen Help)</li>
            <li>&ldquo;historical pear soufflé&rdquo; (Historical recipes)</li>
            <li>&ldquo;more&rdquo; / &ldquo;back&rdquo; when there are lots of results</li>
            <li>&ldquo;show me all the vegan recipes&rdquo;, then &ldquo;only mine&rdquo;, &ldquo;quick ones&rdquo;, &ldquo;under 45 minutes&rdquo;, &ldquo;easy ones&rdquo;, &ldquo;show everything&rdquo;</li>
            <li>&ldquo;number 2&rdquo; to open a result · &ldquo;save these as my vegan list&rdquo;</li>
            <li>&ldquo;profile&rdquo; / &ldquo;settings&rdquo;, &ldquo;pricing&rdquo;</li>
            <li>&ldquo;directory&rdquo;, &ldquo;news&rdquo;, &ldquo;FAQs&rdquo;</li>
          </ul>
        </div>
        <div>
          <p className="font-bold text-rf-ink mb-1">Open a recipe or cookbook</p>
          <ul className="list-disc list-inside text-rf-ink-soft text-xs space-y-1">
            <li>&ldquo;open my brownies cookbook&rdquo;</li>
            <li>&ldquo;cook paella&rdquo; (jumps straight into Kitchen View)</li>
            <li>&ldquo;print my Christmas cookbook&rdquo;</li>
            <li>&ldquo;find chocolate chip cookies&rdquo;</li>
          </ul>
        </div>
        <div>
          <p className="font-bold text-rf-ink mb-1">Shopping list — chain multiple items</p>
          <ul className="list-disc list-inside text-rf-ink-soft text-xs space-y-1">
            <li>&ldquo;add bread and milk to the list&rdquo;</li>
            <li>&ldquo;add a loaf of bread, 2 litres of milk, and a bunch of parsley&rdquo;</li>
            <li>&ldquo;mark eggs and milk as bought&rdquo; / &ldquo;tick off eggs and milk&rdquo;</li>
            <li>&ldquo;unmark eggs and milk&rdquo; · &ldquo;clear bought items&rdquo;</li>
            <li>&ldquo;remove eggs&rdquo; · &ldquo;delete milk and bread&rdquo; · &ldquo;take butter off the list&rdquo;</li>
            <li><b>&ldquo;undo&rdquo;</b> / &ldquo;undo that&rdquo; / &ldquo;scratch that&rdquo; — reverses the last voice change (say it twice to peel back a chain)</li>
            <li>Already have an item? When you re-add it you&rsquo;ll be asked: say <b>add</b>, <b>replace</b>, <b>delete</b> or <b>cancel</b></li>
            <li className="italic opacity-80">(Tip: avoid chaining compound names like &ldquo;sweet and sour sauce&rdquo; — say them one at a time.)</li>
          </ul>
        </div>
        <div>
          <p className="font-bold text-rf-ink mb-1">Global actions</p>
          <ul className="list-disc list-inside text-rf-ink-soft text-xs space-y-1">
            <li>&ldquo;help&rdquo; — this sheet</li>
            <li>&ldquo;back&rdquo; / &ldquo;go back&rdquo; — browser back</li>
            <li>&ldquo;log out&rdquo; / &ldquo;sign out&rdquo;</li>
            <li>&ldquo;cancel&rdquo; / &ldquo;never mind&rdquo; — close overlay</li>
          </ul>
        </div>
        <div>
          <p className="font-bold text-rf-ink mb-1">Multiple matches?</p>
          <p className="text-rf-ink-soft text-xs leading-relaxed">Say the number: &ldquo;the first one&rdquo;, &ldquo;second&rdquo;, &ldquo;number three&rdquo;.</p>
        </div>
        <div className="pt-3 border-t border-rf-ink/10">
          <p className="text-rf-ink-soft text-xs">
            Note: inside Kitchen View this overlay stands down. The cook-mode mic commands (next step, show ingredients, scroll down, etc.) keep working as always.
          </p>
        </div>
      </div>
    </>
  );
}
